// src/hooks/useWeb3.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPublicClient, createWalletClient, http, custom, fallback } from 'viem';
import { mainnet } from 'viem/chains';
import { CONFIG } from '../config/constants';

// ============================================
//    W E B 3   H O O K
//    Ethereum Mainnet • Trustless
//    Uses constants.js - No env variables
// ============================================

const MAINNET_CHAIN_ID = '0x1'; // Ethereum Mainnet

// Use RPCs from constants.js
const RPC_URLS = [
  CONFIG.RPC_URL,
  ...CONFIG.FALLBACK_RPCS
].filter((url, index, self) => self.indexOf(url) === index);

const createResilientPublicClient = () => {
  return createPublicClient({
    chain: mainnet,
    transport: fallback(
      RPC_URLS.map(url =>
        http(url, {
          timeout: 10_000,
          retryCount: 3,
          retryDelay: 1000,
        })
      ),
      { rank: false }
    )
  });
};

export const useWeb3 = () => {
  const [account, setAccount] = useState(null);
  const [publicClient, setPublicClient] = useState(null);
  const [walletClient, setWalletClient] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isWrongNetwork, setIsWrongNetwork] = useState(false);
  const [rpcStatus, setRpcStatus] = useState('connecting');

  // Use refs to avoid stale closures in event listeners
  const accountRef = useRef(account);
  const chainIdRef = useRef(chainId);
  const hasManuallyDisconnected = useRef(false);

  useEffect(() => {
    accountRef.current = account;
    chainIdRef.current = chainId;
  }, [account, chainId]);

  const contracts = CONFIG.ADDRESSES;

  // ── Initialize public client ──────────────────────────────────────────────
  useEffect(() => {
    const initPublicClient = async () => {
      try {
        const client = createResilientPublicClient();
        await client.getBlockNumber();
        setPublicClient(client);
        setRpcStatus('ok');
        console.log('Public client initialized');
      } catch (error) {
        console.warn('RPC connection failed:', error.message);
        setPublicClient(createResilientPublicClient());
        setRpcStatus('failed');
      }
    };
    initPublicClient();
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const checkWrongNetwork = useCallback((currentChainId) => {
    return currentChainId !== MAINNET_CHAIN_ID;
  }, []);

  const updateWalletClient = useCallback((currentAccount, currentChainId) => {
    if (!window.ethereum || !currentAccount || currentChainId !== MAINNET_CHAIN_ID) {
      setWalletClient(null);
      return;
    }
    try {
      const wallet = createWalletClient({
        account: currentAccount,
        chain: mainnet,
        transport: custom(window.ethereum)
      });
      setWalletClient(wallet);
      console.log('Wallet client created for mainnet');
    } catch (error) {
      console.error('Error creating wallet client:', error);
      setWalletClient(null);
    }
  }, []);

  // ── Check existing connection on load ─────────────────────────────────────
  useEffect(() => {
    if (!window.ethereum) return;

    const checkConnection = async () => {
      // Skip auto-reconnect if user manually disconnected
      if (hasManuallyDisconnected.current) {
        console.log('Skipping auto-reconnect: user manually disconnected');
        return;
      }
      
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          const currentChainId = await window.ethereum.request({ method: 'eth_chainId' });
          const isWrong = checkWrongNetwork(currentChainId);

          setAccount(accounts[0]);
          setChainId(currentChainId);
          setIsWrongNetwork(isWrong);
          updateWalletClient(accounts[0], currentChainId);
          console.log('Auto-reconnected to existing session');
        }
      } catch (error) {
        console.error('Error checking connection:', error);
      }
    };

    checkConnection();
  }, [checkWrongNetwork, updateWalletClient]);

  // ── Event listeners ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        setAccount(null);
        setWalletClient(null);
        setIsWrongNetwork(false);
        hasManuallyDisconnected.current = true; // User disconnected via MetaMask
      } else {
        const newAccount = accounts[0];
        setAccount(newAccount);
        window.ethereum.request({ method: 'eth_chainId' }).then((currentChainId) => {
          setChainId(currentChainId);
          setIsWrongNetwork(checkWrongNetwork(currentChainId));
          updateWalletClient(newAccount, currentChainId);
        });
      }
    };

    const handleChainChanged = (newChainId) => {
      console.log('Chain changed to:', newChainId);
      setChainId(newChainId);
      const isWrong = checkWrongNetwork(newChainId);
      setIsWrongNetwork(isWrong);
      if (accountRef.current) {
        updateWalletClient(accountRef.current, newChainId);
      }
    };

    const handleDisconnect = () => {
      setAccount(null);
      setWalletClient(null);
      setChainId(null);
      setIsWrongNetwork(false);
      hasManuallyDisconnected.current = true;
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);
    window.ethereum.on('disconnect', handleDisconnect);

    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
      window.ethereum.removeListener('disconnect', handleDisconnect);
    };
  }, [checkWrongNetwork, updateWalletClient]);

  // ── Connect wallet ────────────────────────────────────────────────────────
  const connectWallet = async () => {
    if (!window.ethereum) {
      alert('Please install MetaMask');
      return;
    }

    try {
      // Reset disconnect flag - allow auto-reconnect going forward
      hasManuallyDisconnected.current = false;
      
      // Request accounts - MetaMask shows picker if not already connected
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const currentChainId = await window.ethereum.request({ method: 'eth_chainId' });
      const isWrong = checkWrongNetwork(currentChainId);

      setChainId(currentChainId);
      setIsWrongNetwork(isWrong);

      if (isWrong) {
        // Set account so we know who is connected and can target a network switch,
        // but DO NOT create a wallet client — nothing writable on wrong chain.
        // The WrongNetworkBanner handles the next step.
        setAccount(accounts[0]);
        setWalletClient(null);
        console.log('Connected on wrong network — banner will prompt switch');
        return;
      }

      // Correct network ✓
      setAccount(accounts[0]);
      const wallet = createWalletClient({
        account: accounts[0],
        chain: mainnet,
        transport: custom(window.ethereum)
      });
      setWalletClient(wallet);
      console.log('Wallet connected on mainnet');

    } catch (error) {
      if (error.code !== 4001) {
        console.error('Connection error:', error);
        alert('Error connecting wallet. Please try again.');
      }
    }
  };

  // ── Disconnect ────────────────────────────────────────────────────────────
  const disconnect = async () => {
    // Set flag to prevent auto-reconnect
    hasManuallyDisconnected.current = true;
    
    try {
      // Revoke eth_accounts permission (MetaMask v10.8.0+)
      await window.ethereum.request({
        method: 'wallet_revokePermissions',
        params: [{ eth_accounts: {} }]
      });
      console.log('Wallet permissions revoked');
    } catch (error) {
      // Older MetaMask versions don't support this - that's fine
      // The flag will still prevent auto-reconnect
      console.log('Permission revocation not supported, clearing state only');
    }
    
    // Clear all state
    setAccount(null);
    setWalletClient(null);
    setChainId(null);
    setIsWrongNetwork(false);
  };

  // ── Switch to mainnet ─────────────────────────────────────────────────────
  const switchToMainnet = async () => {
    if (!window.ethereum) return;
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: MAINNET_CHAIN_ID }]
      });
      // chainChanged event will fire → handleChainChanged → creates wallet client
    } catch (error) {
      if (error.code === 4902) {
        alert('Ethereum Mainnet is not available in your wallet. Please add it manually.');
      } else if (error.code !== 4001) {
        alert('Failed to switch network. Please switch manually in your wallet.');
      }
    }
  };

  // ── Retry public RPC ──────────────────────────────────────────────────────
  const retryRPC = async () => {
    setRpcStatus('connecting');
    try {
      const client = createResilientPublicClient();
      await client.getBlockNumber();
      setPublicClient(client);
      setRpcStatus('ok');
    } catch {
      setRpcStatus('failed');
    }
  };

  return {
    account,
    publicClient,
    walletClient,
    chainId,
    contracts,
    // ⚠️  isConnected is only true when on the CORRECT network.
    // This prevents the app treating a wrong-network session as fully connected.
    isConnected: !!account && !isWrongNetwork,
    isWrongNetwork,
    rpcStatus,
    connectWallet,
    disconnect,
    switchToMainnet,
    retryRPC,
    chainInfo: CONFIG.CHAIN_INFO
  };
};